import { AthenaAuthRuntimeError } from "../../auth/local/errors.ts";
import type { AuthMemberRow } from "../../auth/local/models.ts";
import {
	FOUNDING_OWNER_REMOVE_FORBIDDEN,
	FOUNDING_OWNER_ROLE_LOCKED,
} from "../../auth/local/organization-invariants.ts";
import { type AthenaRightKey, parseAthenaRightKey } from "../../rights/key.ts";
import { capabilitiesFromRights } from "./capabilities.ts";
import {
	AUTHORIZATION_CATALOG_VERSION,
	listAthenaAuthorizationRights,
} from "./catalog.ts";
import { assertCloneRoleTenantBoundary } from "./clone-source.ts";
import { toRoleDescriptor, toRoleDetail } from "./role-descriptor.ts";
import {
	assertExpectedVersion,
	assertProtectedRoleImmutable,
	assertRightsReplaceAllowed,
	throwReassignmentInvalid,
	throwRoleHasAssignments,
	throwRoleNotFound,
} from "./role-invariants.ts";
import { projectAssignableRoles } from "./snapshot-catalog.ts";
import type { AthenaAuthorizationStore } from "./store.ts";
import {
	BUILTIN_AUTHORIZATION_ROLES,
	builtinRoleRecord,
	mapLegacyMemberRole,
	mapLegacyUserRole,
	ORGANIZATION_OWNER_ROLE,
	resolveMemberAssignmentRole,
} from "./templates.ts";
import type {
	AssignedRoleSummary,
	AthenaAuthorizationRoleDetail,
	AthenaAuthorizationRoleRecord,
	AuthorizationSnapshot,
	MemberAuthorizationActions,
	RoleDescriptor,
} from "./types.ts";

function uniqueRights(keys: readonly string[]): AthenaRightKey[] {
	return [...new Set(keys)].map((key) => parseAthenaRightKey(key));
}

export class MemoryAuthorizationStore implements AthenaAuthorizationStore {
	private readonly audit: Record<string, unknown>[] = [];
	private readonly memberRoles = new Map<string, string>();
	private readonly organizationRevisions = new Map<string, number>();
	private readonly platformRevision = { value: 1 };
	private readonly roleRights = new Map<string, Set<string>>();
	private readonly roles = new Map<string, AthenaAuthorizationRoleRecord>();
	private readonly userRoles = new Map<string, string>();
	private materialized = false;

	async materialize(): Promise<void> {
		for (const template of BUILTIN_AUTHORIZATION_ROLES) {
			this.roles.set(template.id, builtinRoleRecord(template));
			this.roleRights.set(template.id, new Set(template.rights));
		}
		this.materialized = true;
		void AUTHORIZATION_CATALOG_VERSION;
		void listAthenaAuthorizationRights();
	}

	async hasUserAssignment(userId: string): Promise<boolean> {
		await this.ensure();
		return this.userRoles.has(userId);
	}

	async assignUserRole(
		userId: string,
		roleKey: string,
		assignedBy?: string,
	): Promise<void> {
		await this.ensure();
		const role = this.requireRole(roleKey);
		if (role.scopeKind !== "platform") {
			throw new Error(`Role ${roleKey} is not a platform role`);
		}
		this.userRoles.set(userId, role.id);
		this.platformRevision.value += 1;
		this.audit.push({
			action: "user.role.assign",
			actor_user_id: assignedBy ?? null,
			after_state: { roleKey },
			target_id: userId,
			target_kind: "user",
		});
	}

	async assignMemberRole(
		memberId: string,
		roleKey: string,
		assignedBy?: string,
		organizationId?: string | null,
	): Promise<void> {
		await this.ensure();
		const role = this.resolveOrganizationRole(roleKey, organizationId);
		this.memberRoles.set(memberId, role.id);
		if (organizationId) {
			this.bumpOrganizationRevision(organizationId);
		}
		this.audit.push({
			action: "member.role.assign",
			actor_user_id: assignedBy ?? null,
			after_state: { roleKey: role.key },
			organization_id: organizationId ?? null,
			target_id: memberId,
			target_kind: "member",
		});
	}

	async lookupAssignableOrganizationRole(
		organizationId: string,
		roleRef: string,
	): Promise<AthenaAuthorizationRoleRecord | undefined> {
		await this.ensure();
		try {
			return this.resolveOrganizationRole(roleRef, organizationId);
		} catch {
			return undefined;
		}
	}

	async cloneRole(input: {
		actorUserId: string;
		name: string;
		organizationId?: string | null;
		sourceRoleId: string;
	}): Promise<AthenaAuthorizationRoleRecord> {
		await this.ensure();
		const source = this.roles.get(input.sourceRoleId);
		if (!source) {
			throw AthenaAuthRuntimeError.badRequest("Source role not found");
		}
		const targetOrganizationId = input.organizationId ?? source.organizationId;
		assertCloneRoleTenantBoundary({
			sourceOrganizationId: source.organizationId,
			sourceScopeKind: source.scopeKind,
			targetOrganizationId,
		});
		const id = crypto.randomUUID();
		const record: AthenaAuthorizationRoleRecord = {
			assignable: true,
			id,
			key: `custom_${id.slice(0, 8)}`,
			name: input.name.trim() || `${source.name} copy`,
			organizationId: targetOrganizationId,
			protected: false,
			scopeKind: source.scopeKind,
			systemKind: null,
			version: 1,
		};
		if (source.scopeKind === "organization") {
			record.organizationId = targetOrganizationId;
		}
		this.roles.set(id, record);
		this.roleRights.set(id, new Set(this.roleRights.get(source.id) ?? []));
		if (record.organizationId) {
			this.bumpOrganizationRevision(record.organizationId);
		} else {
			this.platformRevision.value += 1;
		}
		const rights = [...(this.roleRights.get(id) ?? [])].sort();
		this.audit.push({
			action: "role.clone",
			actor_user_id: input.actorUserId,
			after_state: { rights },
			organization_id: record.organizationId,
			target_id: id,
			target_kind: "role",
		});
		return record;
	}

	async createRole(input: {
		actorUserId: string;
		name: string;
		organizationId?: string | null;
		rights?: readonly string[];
		scopeKind: "platform" | "organization";
	}): Promise<AthenaAuthorizationRoleRecord> {
		await this.ensure();
		const name = input.name.trim();
		if (!name) {
			throw AthenaAuthRuntimeError.badRequest("name is required");
		}
		if (input.scopeKind === "organization" && !input.organizationId) {
			throw AthenaAuthRuntimeError.badRequest("organizationId is required");
		}
		const rights = input.rights ?? [];
		assertRightsReplaceAllowed({
			organizationId: input.organizationId,
			rights,
			roleScope: input.scopeKind,
		});
		const id = crypto.randomUUID();
		const record: AthenaAuthorizationRoleRecord = {
			assignable: true,
			id,
			key: `custom_${id.slice(0, 8)}`,
			name,
			organizationId:
				input.scopeKind === "organization" ? (input.organizationId ?? null) : null,
			protected: false,
			scopeKind: input.scopeKind,
			systemKind: null,
			version: 1,
		};
		this.roles.set(id, record);
		this.roleRights.set(
			id,
			new Set(rights.map((key) => parseAthenaRightKey(key))),
		);
		this.bumpScopeRevision(record.organizationId);
		this.audit.push({
			action: "role.create",
			actor_user_id: input.actorUserId,
			after_state: { rights: [...rights].sort() },
			organization_id: record.organizationId,
			target_id: id,
			target_kind: "role",
		});
		return record;
	}

	async getRole(
		id: string,
		organizationId?: string | null,
	): Promise<AthenaAuthorizationRoleDetail | undefined> {
		await this.ensure();
		const role = this.roles.get(id);
		if (!role || !this.roleVisibleInScope(role, organizationId)) {
			return undefined;
		}
		const rights = uniqueRights([...(this.roleRights.get(id) ?? [])]);
		return toRoleDetail(role, rights, this.assignmentCount(role.id));
	}

	async listRoles(input: {
		organizationId?: string | null;
		scopeKind: "platform" | "organization";
	}): Promise<readonly RoleDescriptor[]> {
		await this.ensure();
		return [...this.roles.values()]
			.filter((role) => {
				if (role.scopeKind !== input.scopeKind) {
					return false;
				}
				return this.roleVisibleInScope(role, input.organizationId);
			})
			.map((role) =>
				toRoleDescriptor(role, {
					assignmentCount: this.assignmentCount(role.id),
					rightCount: this.roleRights.get(role.id)?.size ?? 0,
				}),
			);
	}

	async listRoleRights(roleId: string): Promise<readonly AthenaRightKey[]> {
		await this.ensure();
		return uniqueRights([...(this.roleRights.get(roleId) ?? [])]);
	}

	async updateRole(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		name: string;
		organizationId?: string | null;
	}): Promise<AthenaAuthorizationRoleRecord> {
		await this.ensure();
		const role = this.requireScopedRole(input.id, input.organizationId);
		assertProtectedRoleImmutable(role);
		assertExpectedVersion(role, input.expectedVersion);
		const name = input.name.trim();
		if (!name) {
			throw AthenaAuthRuntimeError.badRequest("name is required");
		}
		const next = { ...role, name, version: role.version + 1 };
		this.roles.set(role.id, next);
		this.bumpScopeRevision(next.organizationId);
		this.audit.push({
			action: "role.rename",
			actor_user_id: input.actorUserId,
			after_state: { name },
			before_state: { name: role.name },
			organization_id: next.organizationId,
			target_id: next.id,
			target_kind: "role",
		});
		return next;
	}

	async replaceRoleRights(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		organizationId?: string | null;
		rights: readonly string[];
	}): Promise<AthenaAuthorizationRoleRecord> {
		await this.ensure();
		const role = this.requireScopedRole(input.id, input.organizationId);
		assertProtectedRoleImmutable(role);
		assertExpectedVersion(role, input.expectedVersion);
		assertRightsReplaceAllowed({
			organizationId: role.organizationId,
			rights: input.rights,
			roleScope: role.scopeKind,
		});
		const before = [...(this.roleRights.get(role.id) ?? [])].sort();
		const after = [...new Set(input.rights)].sort();
		this.roleRights.set(
			role.id,
			new Set(after.map((key) => parseAthenaRightKey(key))),
		);
		const next = { ...role, version: role.version + 1 };
		this.roles.set(role.id, next);
		this.bumpScopeRevision(next.organizationId);
		this.audit.push({
			action: "role.rights.replace",
			actor_user_id: input.actorUserId,
			after_state: { rights: after },
			before_state: { rights: before },
			organization_id: next.organizationId,
			target_id: next.id,
			target_kind: "role",
		});
		return next;
	}

	async deleteRole(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		organizationId?: string | null;
		reassignmentRoleId?: string | null;
	}): Promise<void> {
		await this.ensure();
		const role = this.requireScopedRole(input.id, input.organizationId);
		assertProtectedRoleImmutable(role);
		assertExpectedVersion(role, input.expectedVersion);
		const assigned = this.assignmentCount(role.id);
		if (assigned > 0) {
			const replacementId = input.reassignmentRoleId?.trim() ?? "";
			if (!replacementId) {
				throwRoleHasAssignments();
			}
			const replacement = this.requireScopedRole(
				replacementId,
				input.organizationId,
			);
			if (
				replacement.id === role.id ||
				!replacement.assignable ||
				replacement.scopeKind !== role.scopeKind
			) {
				throwReassignmentInvalid();
			}
			if (role.scopeKind === "organization") {
				for (const [memberId, roleId] of this.memberRoles) {
					if (roleId === role.id) {
						this.memberRoles.set(memberId, replacement.id);
					}
				}
			} else {
				for (const [userId, roleId] of this.userRoles) {
					if (roleId === role.id) {
						this.userRoles.set(userId, replacement.id);
					}
				}
			}
		}
		const before = [...(this.roleRights.get(role.id) ?? [])].sort();
		this.roles.delete(role.id);
		this.roleRights.delete(role.id);
		this.bumpScopeRevision(role.organizationId);
		this.audit.push({
			action: "role.delete",
			actor_user_id: input.actorUserId,
			before_state: { rights: before },
			organization_id: role.organizationId,
			target_id: role.id,
			target_kind: "role",
		});
	}

	async listAudit(input: {
		limit?: number;
		organizationId?: string | null;
	}): Promise<readonly Record<string, unknown>[]> {
		const scoped = this.audit.filter((entry) => {
			if (input.organizationId === undefined) {
				return true;
			}
			if (input.organizationId === null) {
				return entry.organization_id == null;
			}
			return entry.organization_id === input.organizationId;
		});
		return scoped.slice(-(input.limit ?? 50));
	}

	async resolveEffectiveRights(input: {
		activeOrganizationId?: string | null;
		getMember: (
			organizationId: string,
			userId: string,
		) => Promise<AuthMemberRow | undefined>;
		userId: string;
	}): Promise<readonly AthenaRightKey[]> {
		await this.ensure();
		const platformRoleId = this.userRoles.get(input.userId);
		const rights = new Set<string>();
		this.addRoleRights(platformRoleId, rights);
		const organizationId = input.activeOrganizationId?.trim() ?? "";
		if (organizationId.length > 0) {
			const member = await input.getMember(organizationId, input.userId);
			if (member) {
				const memberRoleId =
					this.memberRoles.get(member.id) ??
					this.requireRole(mapLegacyMemberRole(member.role)).id;
				this.addRoleRights(memberRoleId, rights);
			}
		}
		return uniqueRights([...rights]);
	}

	async readSnapshot(input: {
		activeOrganizationId?: string | null;
		foundingOwnerUserId?: string | null;
		getMember: (
			organizationId: string,
			userId: string,
		) => Promise<AuthMemberRow | undefined>;
		listMembers: (organizationId: string) => Promise<AuthMemberRow[]>;
		userId: string;
	}): Promise<AuthorizationSnapshot> {
		const effectiveRights = await this.resolveEffectiveRights(input);
		const capabilities = capabilitiesFromRights(effectiveRights);
		const roles: AssignedRoleSummary[] = [];
		const platformRoleId = this.userRoles.get(input.userId);
		if (platformRoleId) {
			const role = this.roles.get(platformRoleId);
			if (role) {
				roles.push({
					displayName: role.name,
					key: role.key,
					scopeKind: role.scopeKind,
				});
			}
		}
		const organizationId = input.activeOrganizationId?.trim() ?? "";
		if (organizationId.length > 0) {
			const member = await input.getMember(organizationId, input.userId);
			if (member) {
				const roleId = this.memberRoles.get(member.id);
				const role = roleId ? this.roles.get(roleId) : undefined;
				if (role) {
					roles.push({
						displayName: role.name,
						key: role.key,
						scopeKind: role.scopeKind,
					});
				}
			}
		}
		const members =
			organizationId.length > 0 ? await input.listMembers(organizationId) : [];
		const assignableRoles = projectAssignableRoles({
			activeOrganizationId: organizationId,
			capabilities,
			roles: this.listAssignableRoleDescriptors(organizationId, members.length),
		});
		return {
			...(organizationId.length > 0
				? { activeOrganizationId: organizationId }
				: {}),
			assignableRoles,
			capabilities,
			effectiveRights,
			revision: organizationId
				? (this.organizationRevisions.get(organizationId) ?? 1)
				: this.platformRevision.value,
			roles,
		};
	}

	async annotateMember(
		member: AuthMemberRow,
		actor: {
			foundingOwnerUserId: string | null;
			getMember: (
				organizationId: string,
				userId: string,
			) => Promise<AuthMemberRow | undefined>;
			userId: string;
		},
	): Promise<MemberAuthorizationActions> {
		await this.ensure();
		const roleId =
			this.memberRoles.get(member.id) ??
			this.requireRole(mapLegacyMemberRole(member.role)).id;
		const role = this.requireRoleById(roleId);
		const actorRights = await this.resolveEffectiveRights({
			activeOrganizationId: member.organization_id,
			getMember: actor.getMember,
			userId: actor.userId,
		});
		const capabilities = capabilitiesFromRights(actorRights);
		const denialReasons: MemberAuthorizationActions["denialReasons"] = {};
		if (actor.foundingOwnerUserId === member.user_id) {
			denialReasons.changeRole = FOUNDING_OWNER_ROLE_LOCKED;
			denialReasons.remove = FOUNDING_OWNER_REMOVE_FORBIDDEN;
		} else if (
			actor.userId === member.user_id &&
			role.key === ORGANIZATION_OWNER_ROLE
		) {
			denialReasons.changeRole =
				"Owners can't change their own role from the members table.";
			denialReasons.remove =
				"Owners can't suspend or remove themselves from the members table.";
		} else if (!capabilities.canChangeMemberRole) {
			denialReasons.changeRole = "Not allowed to change member roles.";
			denialReasons.remove = "Not allowed to remove members.";
		}
		return {
			canChangeRole: denialReasons.changeRole === undefined,
			canRemove: denialReasons.remove === undefined,
			denialReasons,
			roleDisplayName: role.name,
			roleKey: role.key,
		};
	}

	async ensureUserAssigned(
		userId: string,
		legacyRole?: string | null,
	): Promise<void> {
		await this.ensure();
		if (!this.userRoles.has(userId)) {
			await this.assignUserRole(userId, mapLegacyUserRole(legacyRole));
		}
	}

	async ensureMemberAssigned(
		memberId: string,
		legacyRole?: string | null,
	): Promise<void> {
		await this.ensure();
		if (!this.memberRoles.has(memberId)) {
			await this.assignMemberRole(memberId, mapLegacyMemberRole(legacyRole));
		}
	}

	private listAssignableRoleDescriptors(
		organizationId: string,
		memberCount: number,
	): RoleDescriptor[] {
		void memberCount;
		return [...this.roles.values()]
			.filter((role) => role.assignable)
			.filter(
				(role) =>
					role.organizationId === null ||
					(organizationId.length > 0 && role.organizationId === organizationId),
			)
			.map((role) =>
				toRoleDescriptor(role, {
					assignmentCount: this.assignmentCount(role.id),
					rightCount: this.roleRights.get(role.id)?.size ?? 0,
				}),
			);
	}

	private requireScopedRole(
		roleId: string,
		organizationId?: string | null,
	): AthenaAuthorizationRoleRecord {
		const role = this.roles.get(roleId);
		if (!role || !this.roleVisibleInScope(role, organizationId)) {
			throwRoleNotFound();
		}
		return role;
	}

	private roleVisibleInScope(
		role: AthenaAuthorizationRoleRecord,
		organizationId?: string | null,
	): boolean {
		if (organizationId) {
			if (role.scopeKind !== "organization") {
				return false;
			}
			return (
				role.organizationId === null || role.organizationId === organizationId
			);
		}
		return role.scopeKind === "platform" && role.organizationId === null;
	}

	private assignmentCount(roleId: string): number {
		const role = this.roles.get(roleId);
		if (!role) {
			return 0;
		}
		if (role.scopeKind === "organization") {
			return [...this.memberRoles.values()].filter((id) => id === roleId).length;
		}
		return [...this.userRoles.values()].filter((id) => id === roleId).length;
	}

	private bumpScopeRevision(organizationId: string | null): void {
		if (organizationId) {
			this.bumpOrganizationRevision(organizationId);
			return;
		}
		this.platformRevision.value += 1;
	}

	private resolveOrganizationRole(
		roleRef: string,
		organizationId?: string | null,
	): AthenaAuthorizationRoleRecord {
		const mapped = resolveMemberAssignmentRole(roleRef);
		const matches = [...this.roles.values()].filter((role) => {
			if (role.scopeKind !== "organization" || !role.assignable) {
				return false;
			}
			const hit =
				role.id === roleRef || role.key === roleRef || role.key === mapped;
			if (!hit) {
				return false;
			}
			return (
				role.organizationId === null || role.organizationId === organizationId
			);
		});
		const specific = matches.find(
			(role) => role.organizationId === organizationId,
		);
		const template = matches.find((role) => role.organizationId === null);
		const role = specific ?? template;
		if (!role) {
			throw new Error(`Unknown role ${roleRef}`);
		}
		return role;
	}

	private bumpOrganizationRevision(organizationId: string): void {
		this.organizationRevisions.set(
			organizationId,
			(this.organizationRevisions.get(organizationId) ?? 1) + 1,
		);
	}

	private addRoleRights(roleId: string | undefined, into: Set<string>): void {
		if (!roleId) {
			return;
		}
		for (const key of this.roleRights.get(roleId) ?? []) {
			into.add(key);
		}
	}

	private requireRole(roleKey: string): AthenaAuthorizationRoleRecord {
		for (const role of this.roles.values()) {
			if (role.key === roleKey && role.organizationId === null) {
				return role;
			}
		}
		throw new Error(`Unknown role ${roleKey}`);
	}

	private requireRoleById(roleId: string): AthenaAuthorizationRoleRecord {
		const role = this.roles.get(roleId);
		if (!role) {
			throw new Error(`Unknown role id ${roleId}`);
		}
		return role;
	}

	private async ensure(): Promise<void> {
		if (!this.materialized) {
			await this.materialize();
		}
	}
}
